<template>
  <div>
    <md-card id="forsetTable">
      <md-card-header>
        <div class="md-title">Plot A {{ area }} m² r={{ radius }}</div>
      </md-card-header>

      <md-table v-model="forest" ref="tab" :key="update">
        <md-table-row slot="md-table-row" slot-scope="{ item }">
          <md-table-cell md-label="#" md-numeric>{{ item.id }}</md-table-cell>

          <md-table-cell md-label="n°">
            <md-field :class="!checkRange(item.number, 0, 999) ? 'md-invalid' : ''">
              <md-input type="number" name="number" v-model="item.number" />
              <span class="md-error">{{ $t("error.errorRange") }} (0-999)</span>
            </md-field>
          </md-table-cell>

          <md-table-cell :md-label="$t('typeInfo.species')">
            <md-field style="width: 300px">
              <std-autocomplete
                v-if="loadStd"
                :data="stdList"
                :label="$t('typeInfo.woodSpecies')"
                :initialValue="item.specie_std2"
                :index="item"
                :initialDisplay="initSpecie[item.id]"
                @result="getSpecie"></std-autocomplete>
            </md-field>
          </md-table-cell>

          <md-table-cell :md-label="$t('typeInfo.diameter') + ' (cm)'">
            <md-field :class="!checkDbh(item.avg_diameter) ? 'md-invalid' : ''">
              <md-input type="number" step="0.01" name="avg_diameter" v-model="item.avg_diameter" />
              <span class="md-error">{{ $t("error.errorRange") }} (0-5000)</span>
            </md-field>
          </md-table-cell>

          <md-table-cell md-label="H (m)">
            <md-field>
              <select v-model="item.avg_height" name="h">
                <option v-for="(h, i) in hList" :value="h" :key="i">{{ h }}</option>
              </select>
            </md-field>
          </md-table-cell>

          <md-table-cell :md-label="$t('typeInfo.azimuth')">
            <md-field :class="!checkAzimuth(item.azimuth) ? 'md-invalid' : ''">
              <md-input type="number" name="h" v-model="item.azimuth" />
              <span class="md-error">{{ $t("error.errorRange") }} (0°-360°)</span>
            </md-field>
          </md-table-cell>

          <md-table-cell md-label="Dist (m)">
            <md-field :class="!checkDist(item.dist) ? 'md-invalid' : ''">
              <md-input type="number" step="0.01" name="dist" v-model="item.dist" />
              <md-icon @click.native="calcDist(item)">gps_fixed</md-icon>
              <span class="md-error">{{ $t("error.errorRange") }} (0-70)</span>
            </md-field>
          </md-table-cell>

          <md-table-cell :md-label="$t('typeInfo.quality')">
            <md-field>
              <select v-model="item.timber_quality" name="timberQuality">
                <option v-for="(q, key) in timberQualityList" :value="key" :key="key">{{ q }}</option>
              </select>
            </md-field>
          </md-table-cell>

          <md-table-cell :md-label="$t('typeInfo.damage')">
            <md-button @click="openDamage(item)" class="md-primary md-raised">{{ $t("typeInfo.addDamage") }}</md-button>
          </md-table-cell>

          <md-table-cell>
            <md-icon @click.native="copyRow(item)">file_copy</md-icon>
          </md-table-cell>

          <md-table-cell v-if="forest.length > 1">
            <md-icon @click.native="deleteRow(item)">cancel</md-icon>
          </md-table-cell>
        </md-table-row>

        <spinner :load="gpsLoad" />
      </md-table>
    </md-card>

    <md-button class="md-fab md-primary btn-next" @click="addRow()">
      <md-icon>add</md-icon>
    </md-button>

    <damage-dialog :item="row" :show="damage" @close="closeDamage"></damage-dialog>

    <error-dialog :show="error" :text="errorText" @closeError="error = false" />
  </div>
</template>

<script>
import coordsDistance from '@/assets/scripts/coordsDistance'
import DamageDialog from '@/components/plotType/DamageDialog'
import StdAutocomplete from '@/components/StdAutocomplete'
import Spinner from '@/components/Spinner'
import ErrorDialog from '@/components/ErrorDialog'

import { getDomain } from '@/assets/scripts/domainsManager'
import getCoords from '@/assets/scripts/getCoords'
import { formatList, getValue, checkDbh, checkDist, checkAzimuth, checkRange } from '@/assets/scripts/formatData'

const STD2 = 'std2'
const AREA = 'plot_radius'
const TIMBER_QUALITY = 'forest_timber_quality'
const H = 'height_trees'

const ROW = {
  id: 1,
  number: 1,
  specie_std2: null,
  avg_diameter: null,
  avg_height: '',
  azimuth: null,
  dist: null,
  timber_quality: '00',
  damage_type: '',
  damage_level: '',
  damage_dead: ''
}

export default {
  props: ['slope', 'area', 'editTable'],
  components: {
    DamageDialog,
    Spinner,
    ErrorDialog,
    StdAutocomplete
  },
  data: () => ({
    forest: [{ ...ROW }],
    stdList: {},
    timberQualityList: {},
    hList: [],
    id: 1,
    radius: 0,
    damage: false,
    gpsLoad: false,
    error: false,
    errorText: '',
    row: {},
    loadStd: false,
    initSpecie: [],
    update: 0,
    checkDbh: checkDbh,
    checkDist: checkDist,
    checkAzimuth: checkAzimuth,
    checkRange: checkRange
  }),
  mounted () {
    getDomain(STD2).then(data => {
      this.stdList = data[this.$i18n.locale]
      this.stdList = formatList(data[this.$i18n.locale])
      if (this.editTable !== undefined) {
        this.editTable.forEach(forest => {
          this.initSpecie[forest.id] = getValue(this.stdList, forest.specie_std2)
        })
      }
      this.loadStd = true
    })
    getDomain(AREA).then(data => {
      this.radius = data[this.slope][this.area]
    })
    getDomain(TIMBER_QUALITY).then(data => {
      this.timberQualityList = data[this.$i18n.locale]
    })
    getDomain(H).then(data => {
      this.hList = data
    })
  },
  computed: {
    specie () {
      return this.forest[0].specie_std2
    }
  },
  watch: {
    forest: function () {
      this.$emit('forestTable', { area: this.area, forest: this.forest })
    },
    specie: function () {
      this.$emit('forestTable', { area: this.area, forest: this.forest })
    },
    editTable: function () {
      if (this.editTable.length > 0) {
        this.forest = this.editTable
        this.id = this.forest[this.forest.length - 1].id
      }
    }
  },
  methods: {
    addRow: function () {
      let row = { ...ROW }
      row.id = this.id += 1
      this.forest.push(row)
    },

    copyRow: function (row) {
      let item = { ...row }
      item.id = this.id++
      this.initSpecie[this.id] = this.initSpecie[row.id]
      this.forest.push(item)
    },

    deleteRow: function (row) {
      let index = this.forest.indexOf(row)
      this.forest.splice(index, 1)
      this.update += 1
    },

    resetId: function () {
      let id = 1
      this.forest.forEach(forest => {
        forest.id = id
        id += 1
      })
      this.id = id
    },

    getSpecie: function (value, item) {
      item.specie_std2 = value
      this.initSpecie[item.id] = getValue(this.stdList, value)
    },

    openDamage: function (row) {
      this.row = row
      this.damage = true
    },

    closeDamage: function (row) {
      this.damage = false
      this.forest.forEach((item, i) => {
        if (item.id === row.id) {
          this.forest[i] = row
        }
      })
    },

    calcDist: function (item) {
      let plotLatitude = this.$store.state.plot.plot.coords.latitude
      let plotLongitude = this.$store.state.plot.plot.coords.longitude

      if (plotLatitude.length > 0 && plotLongitude.length > 0) {
        this.gpsLoad = true
        getCoords().then(coords => {
          this.gpsLoad = false
          item.dist = coordsDistance(plotLatitude, plotLongitude, coords.latitude, coords.longitude)
        }).catch(err => {
          this.gpsLoad = false
          this.errorText = err
          this.error = true
        })
      } else {
        this.errorText = this.$i18n.t('error.errorDist')
        this.error = true
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  @import '@/assets/style/arcadia_theme.scss';

  .add {
    float: right;
    margin-right: 5px;
  }

  .md-input {
    min-width: 50px;
  }

  select {
    min-width: 150px;
  }
</style>
